import { useEffect, useState } from "react";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import { groupPersistentEdits } from "@/payroll/ai-script/ai-script-persistent-edits-utils";
import {
  useDiscardAllEdits,
  useGetAiScriptCellOverrides,
  useGetAiScriptManualRows,
  useGetAiScriptSpreadsheet,
} from "@/payroll/ai-script/query";
import type {
  IAiScriptEditedCellSelection,
  IAiSpreadsheetCellLocation,
  IAiSpreadsheetCellOverride,
} from "@/payroll/ai-script/types";
import { useWorkbookOperationSnapshot } from "@/payroll/ai-script/workbook-operation";
import { DiscardAllEditsDialog } from "./discard-all-edits-dialog";
import { FocusedEditView } from "./focused-edit-view";
import { ManualEditsList } from "./manual-edits-list";
import { ManualEditsSidebarHeader } from "./manual-edits-sidebar-header";
import {
  ManualEditsEmptyState,
  ManualEditsSkeleton,
} from "./manual-edits-sidebar-states";
import { LoadError, showPersistentEditMutationError } from "./shared";

interface AiScriptPersistentEditsSidebarProps {
  businessId: string | null;
  runId: string;
  isLocked?: boolean;
  isRunning?: boolean;
  focusEdit?: IAiScriptEditedCellSelection | null;
  onShowAll: () => void;
  onClose: () => void;
  onFocusEdit: (override: IAiSpreadsheetCellOverride) => void;
  onGoToCell: (cell: IAiSpreadsheetCellLocation) => void;
  // Fired after ANY successful durable mutation from the panel (discard, restore
  // version, remove row, discard all). These advance the server workbook version
  // outside the session undo stack, so the caller resets that stack — otherwise
  // the next undo/redo sends a stale base_version and 409s.
  onWorkbookMutated?: () => void;
}

export const AiScriptPersistentEditsSidebar = ({
  businessId,
  runId,
  isLocked = false,
  isRunning = false,
  focusEdit,
  onShowAll,
  onClose,
  onFocusEdit,
  onGoToCell,
  onWorkbookMutated,
}: AiScriptPersistentEditsSidebarProps) => {
  const { data, isLoading, isError, refetch } = useGetAiScriptCellOverrides(
    businessId,
    runId,
  );
  const { data: spreadsheetData } = useGetAiScriptSpreadsheet(
    businessId,
    runId,
  );
  const {
    data: manualRowsData,
    isLoading: manualRowsLoading,
    isError: manualRowsError,
    refetch: refetchManualRows,
  } = useGetAiScriptManualRows(businessId, runId);
  const overrides = data?.overrides ?? [];
  const manualRows = manualRowsData?.manual_rows ?? [];
  const workbookOperation = useWorkbookOperationSnapshot(businessId, runId);
  // Both queries feed the panel; keep loading until BOTH settle, and surface an
  // error if EITHER fails (manual rows use retry:false, so a failure is permanent
  // — coercing undefined to [] would falsely show "No manual edits" and hide rows).
  const panelLoading = isLoading || manualRowsLoading;
  const panelError = isError || manualRowsError;
  const retryPanel = () => {
    void refetch();
    void refetchManualRows();
  };
  const sheets = spreadsheetData?.spreadsheet.sheets;
  const isReadOnly = isLocked || isRunning;
  const effectiveSheets = isReadOnly
    ? sheets?.map((sheet) => ({ ...sheet, editable: false }))
    : sheets;
  const { applied, needsReview, dropped } = groupPersistentEdits(overrides);
  // Both cell edits and user-added rows are "manual edits"; the panel is empty
  // only when there are neither.
  const hasNoEdits = overrides.length === 0 && manualRows.length === 0;

  const discardAll = useDiscardAllEdits(businessId);
  const [isDiscardAllOpen, setIsDiscardAllOpen] = useState(false);
  // Show the bulk action when there's anything to discard (edits or added rows —
  // Discard all removes both) and the viewer can edit at least one sheet (falls
  // back to showing while sheets load).
  const canDiscardAll =
    !isReadOnly &&
    !hasNoEdits &&
    (!effectiveSheets || effectiveSheets.some((sheet) => sheet.editable));

  useEffect(() => {
    if (!canDiscardAll) setIsDiscardAllOpen(false);
  }, [canDiscardAll]);

  return (
    <Stack fullWidth align="stretch" className="h-full min-h-0" gap="none">
      <ManualEditsSidebarHeader
        editCount={overrides.length + manualRows.length}
        // Dropped edits are waiting on the same kind of decision as the ones
        // flagged for review, so the header counts them together.
        reviewCount={needsReview.length + dropped.length}
        isRecalculating={workbookOperation.isPending}
        canDiscardAll={canDiscardAll}
        onDiscardAll={() => setIsDiscardAllOpen(true)}
        onClose={onClose}
      />

      <DiscardAllEditsDialog
        open={isDiscardAllOpen && canDiscardAll}
        onOpenChange={setIsDiscardAllOpen}
        isPending={discardAll.isPending}
        onConfirm={() => {
          if (!canDiscardAll) return;
          discardAll.mutate(
            { runId },
            {
              onSuccess: () => {
                setIsDiscardAllOpen(false);
                onWorkbookMutated?.();
              },
              onError: (error) => {
                if (
                  showPersistentEditMutationError(
                    "Couldn't discard all edits",
                    error,
                  )
                ) {
                  setIsDiscardAllOpen(false);
                }
              },
            },
          );
        }}
      />

      {focusEdit ? (
        <ScrollArea className="min-h-0 flex-1">
          <Stack
            gap="none"
            align="stretch"
            className="max-w-[var(--simple-sidebar-width,436px)] px-6 py-5"
          >
            <FocusedEditView
              focus={focusEdit}
              businessId={businessId}
              runId={runId}
              sheets={effectiveSheets}
              onShowAll={onShowAll}
              onGoToCell={onGoToCell}
              onWorkbookMutated={onWorkbookMutated}
            />
          </Stack>
        </ScrollArea>
      ) : panelLoading || panelError || hasNoEdits ? (
        <ScrollArea className="min-h-0 flex-1">
          <Stack
            gap="none"
            align="stretch"
            className="max-w-[var(--simple-sidebar-width,436px)] px-6 py-5"
            data-manual-edits-scroll-content
          >
            {panelLoading ? (
              <ManualEditsSkeleton />
            ) : panelError ? (
              <LoadError
                message="Couldn't load manual edits."
                onRetry={retryPanel}
              />
            ) : (
              <ManualEditsEmptyState />
            )}
          </Stack>
        </ScrollArea>
      ) : (
        <ManualEditsList
          applied={applied}
          needsReview={needsReview}
          dropped={dropped}
          manualRows={manualRows}
          businessId={businessId}
          runId={runId}
          sheets={effectiveSheets}
          onFocusEdit={onFocusEdit}
          onGoToCell={onGoToCell}
          onWorkbookMutated={onWorkbookMutated}
        />
      )}
    </Stack>
  );
};
